import { FC, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

interface DeleteRxConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  rxNumber: string;
  patientName: string;
  isPending: boolean;
}

export const DeleteRxConfirmDialog: FC<DeleteRxConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  rxNumber,
  patientName,
  isPending,
}) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    if (!reason.trim()) {
      setError("Bitte geben Sie einen Grund ein");
      return;
    }
    onConfirm(reason);
  };

  const handleClose = () => {
    if (!isPending) {
      setReason("");
      setError("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: "#9C1A35",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <WarningAmberIcon />
        Rezept entfernen
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Möchten Sie das folgende Rezept wirklich aus dieser Transaktion
            entfernen?
          </Typography>
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "#f5f5f5",
              borderRadius: 2,
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Rezeptnummer
            </Typography>
            <Typography variant="subtitle1" fontWeight={500}>
              {rxNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Patient
            </Typography>
            <Typography variant="subtitle1" fontWeight={500}>
              {patientName}
            </Typography>
          </Box>
        </Box>

        <TextField
          label="Grund für die Entfernung *"
          multiline
          rows={3}
          fullWidth
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setError("");
          }}
          error={!!error}
          helperText={
            error || "Bitte geben Sie den Grund für die Entfernung an"
          }
          disabled={isPending}
          placeholder="z.B. Patient hat Rezept zurückgezogen"
        />

        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, display: "block", color: "#9C1A35" }}
        >
          Hinweis: Das Rezept wird auf "abgeschlossen" gesetzt und ein Ticket
          wird automatisch erstellt.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isPending} variant="outlined">
          Abbrechen
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isPending || !reason.trim()}
          variant="contained"
          sx={{ bgcolor: "#9C1A35", "&:hover": { bgcolor: "#7a1428" } }}
        >
          {isPending ? "Wird entfernt..." : "Rezept entfernen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
